import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import api from '../api/apiClient';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const mapApiNotification = (recipient) => {
  if (!recipient || !recipient.notification) {
    return null;
  }

  return {
    recipientId: recipient.id,
    notificationId: recipient.notification_id,
    title: recipient.notification.title,
    body: recipient.notification.body,
    triggerType: recipient.notification.trigger_type,
    scope: recipient.notification.scope,
    targetRole: recipient.notification.target_role,
    metadata: recipient.notification.metadata || {},
    ticket: recipient.notification.ticket || null,
    isRead: recipient.is_read,
    readAt: recipient.read_at,
    createdAt: recipient.createdAt
  };
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, limit: 10, total: 0 });

  const resetState = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    setPagination({ page: 1, totalPages: 1, limit: 10, total: 0 });
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await api.notifications.summary();
      if (response.data.success) {
        setUnreadCount(response.data.data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Notification summary fetch failed:', error);
    }
  }, [user]);

  const fetchNotifications = useCallback(async (params = {}) => {
    if (!user) {
      resetState();
      return;
    }

    setLoading(true);

    try {
      const query = { page: pagination.page, limit: pagination.limit, ...params };
      const response = await api.notifications.list(query);

      if (!response.data.success) {
        setNotifications([]);
        return;
      }

      const mappedNotifications = (response.data.data.notifications || [])
        .map(mapApiNotification)
        .filter((item) => Boolean(item));

      setNotifications(mappedNotifications);
      setPagination({
        page: response.data.data.pagination.page,
        limit: response.data.data.pagination.limit,
        totalPages: response.data.data.pagination.totalPages,
        total: response.data.data.pagination.total
      });
    } catch (error) {
      console.error('Notifications fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, resetState, user]);

  const refreshNotifications = useCallback(async () => {
    await Promise.all([fetchNotifications({ page: 1 }), fetchSummary()]);
  }, [fetchNotifications, fetchSummary]);

  const handleMarkRead = useCallback(async (notificationId) => {
    if (!notificationId) {
      return;
    }

    setNotifications((current) => current.map((item) => {
      if (item.notificationId !== notificationId) {
        return item;
      }

      if (item.isRead) {
        return item;
      }

      return {
        ...item,
        isRead: true,
        readAt: new Date().toISOString()
      };
    }));

    setUnreadCount((count) => Math.max(count - 1, 0));

    try {
      await api.notifications.markRead(notificationId);
    } catch (error) {
      console.error('Mark notification read failed:', error);
      await refreshNotifications();
    }
  }, [refreshNotifications]);

  const handleMarkUnread = useCallback(async (notificationId) => {
    if (!notificationId) {
      return;
    }

    setNotifications((current) => current.map((item) => {
      if (item.notificationId !== notificationId) {
        return item;
      }

      if (!item.isRead) {
        return item;
      }

      return {
        ...item,
        isRead: false,
        readAt: null
      };
    }));

    setUnreadCount((count) => count + 1);

    try {
      await api.notifications.markUnread(notificationId);
    } catch (error) {
      console.error('Mark notification unread failed:', error);
      await refreshNotifications();
    }
  }, [refreshNotifications]);

  const handleMarkAllRead = useCallback(async () => {
    if (!user) {
      return;
    }

    setNotifications((current) => current.map((item) => ({
      ...item,
      isRead: true,
      readAt: new Date().toISOString()
    })));

    setUnreadCount(0);

    try {
      await api.notifications.markAllRead();
    } catch (error) {
      console.error('Mark all notifications read failed:', error);
      await refreshNotifications();
    }
  }, [refreshNotifications, user]);

  useEffect(() => {
    if (!user) {
      resetState();
      return;
    }

    refreshNotifications();
  }, [refreshNotifications, resetState, user]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    pagination,
    fetchNotifications,
    refreshNotifications,
    handleMarkRead,
    handleMarkUnread,
    handleMarkAllRead
  }), [
    fetchNotifications,
    handleMarkAllRead,
    handleMarkRead,
    handleMarkUnread,
    loading,
    notifications,
    pagination,
    refreshNotifications,
    unreadCount
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }

  return context;
};

